import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { first } from 'rxjs';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AuthService } from '../core/services/auth.service';
import { LanguageService } from '../core/interceptor/language.service';
import { InputComponent } from '../shared/components/input/input.component';
import { ButtonComponent } from '../shared/components/button/button.component';

@Component({
  selector: 'app-reset-password',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    TranslatePipe,
    InputComponent,
    ButtonComponent
  ],
  templateUrl: './reset-password.component.html',
  styleUrl: './reset-password.component.scss'
})
export class ResetPasswordComponent implements OnInit {
  resetPasswordForm!: FormGroup;
  successMessage: string | null = null;
  error: string | null = null;
  key: string | null = null;
  showButtonConfirme: boolean = true;
  loading: boolean = false;
  currentYear: number = new Date().getFullYear();

  constructor(
    private fb: FormBuilder,
    private userService: AuthService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private translate: TranslateService,
    private languageService: LanguageService
  ) {
    this.resetPasswordForm = this.fb.group({
      password: ['', [Validators.required]],
      password2: ['', [Validators.required]],
    });
  }

  ngOnInit(): void {
    this.languageService.initializeTranslation();

    this.activatedRoute.queryParamMap.subscribe((params) => {
      this.key = params.get('key');
      if (!this.key) {
        this.error = this.translate.instant('RESET_PASSWORD.ERRORS.MISSING_KEY');
      }
    });
  }

  get passwordValue(): string {
    return this.resetPasswordForm?.get('password')?.value || '';
  }

  get hasMinLength(): boolean {
    return this.passwordValue.length >= 10;
  }

  get hasUpperCase(): boolean {
    return /[A-Z]/.test(this.passwordValue);
  }

  get hasLowerCase(): boolean {
    return /[a-z]/.test(this.passwordValue);
  }

  get hasSpecialChar(): boolean {
    return /[!@#$%^&*(),.?":{}|<>]/.test(this.passwordValue);
  }

  get isStrengthValid(): boolean {
    return this.hasMinLength && this.hasUpperCase && this.hasLowerCase && this.hasSpecialChar;
  }

  onSubmit(event?: Event): void {
    if (event) {
      event.preventDefault();
    }

    if (!this.key) {
      this.error = this.translate.instant('RESET_PASSWORD.ERRORS.MISSING_KEY');
      return;
    }

    if (!this.resetPasswordForm.valid) {
      this.resetPasswordForm.markAllAsTouched();
      return;
    }

    const password = this.resetPasswordForm.value.password;
    const password2 = this.resetPasswordForm.value.password2;

    this.successMessage = null;
    this.error = null;

    if (password !== password2) {
      this.error = this.translate.instant('RESET_PASSWORD.ERRORS.PASSWORDS_DONT_MATCH');
      return;
    }

    if (!this.isStrengthValid) {
      this.error = this.translate.instant('RESET_PASSWORD.ERRORS.INCORRECT_PASSWORD');
      return;
    }

    this.loading = true;

    this.userService
      .resetForgottentPassword({ key: this.key, newPassword: password })
      .pipe(first())
      .subscribe({
        next: () => {
          this.loading = false;
          this.successMessage = this.translate.instant('RESET_PASSWORD.SUCCESS.PASSWORD_RESET');
          this.showButtonConfirme = false;
          setTimeout(() => {
            this.router.navigate(['/login']);
          }, 4000);
        },
        error: (err: any) => {
          this.loading = false;
          switch (err.status) {
            case 0:
              this.error = this.translate.instant('RESET_PASSWORD.ERRORS_STATUS.CONNECTION_ERROR');
              break;
            case 408:
              this.error = this.translate.instant('RESET_PASSWORD.ERRORS_STATUS.TIMEOUT');
              break;
            case 500:
              this.error = this.translate.instant('RESET_PASSWORD.ERRORS_STATUS.SERVER_ERROR');
              break;
            default:
              this.error = err.message ? err.message : this.translate.instant('RESET_PASSWORD.ERRORS.GENERAL_ERROR');
              break;
          }
        }
      });
  }
}
